<template>
  <div
    v-if="show"
    ref="contaier"
    :class="['intent-editor-container', { rtl: isArabicText() }]"
    @mousedown="mouseDown = true"
    @mouseup="mouseDown = false"
  >
    <v-dialog v-model="deleteEntityDialog" width="400">
      <v-card v-if="selectedEntity">
        <v-card-title class="headline" primary-title>
          Delete {{ selectedEntity.name }}
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this entity from this sample?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :loading="loaders.delete"
            color="error"
            outline
            @click="deleteEntity"
          >
            Delete
          </v-btn>
          <v-btn
            color="black"
            class="white--text"
            @click="deleteEntityDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div
      v-for="(split, s) in utterance"
      :class="['split-item', { right: isArabicText() }]"
      :key="s"
    >
      <span
        :class="[
          'text-split',
          'split-top-level',
          'pointer-cursor',
          { 'selected-split': split.selected },
          { 'highlighted-split': split.highlight },
          { 'split-hovered': split.hover },
          { 'first-selection': isTheFirstSelection(s) && !isArabicText() },
          { 'last-selection': isTheLastSelection(s) && !isArabicText() },
          { 'first-selection-ar': isTheFirstSelection(s) && isArabicText() },
          { 'last-selection-ar': isTheLastSelection(s) && isArabicText() },
        ]"
        @mouseover="handleMouseOver({ index: s, status: true })"
        @mouseleave="handleMouseOver({ index: s, status: false })"
        @click="handleClick({ index: s })"
        >{{ `${split.text}` }}
        <span
          :class="[
            'text-split',
            { 'selected-split': split.selected },
            { 'highlighted-split': split.highlight },
            { 'split-hovered': split.hover },
          ]"
          >{{ `${s === utterance.length - 1 ? "" : " "}` }}</span
        >
      </span>
      <div :class="['split-second-level mt-1']">
        <div
          v-for="(genericSample, gs) in samples"
          :key="gs"
          style="display: block; height: 20px"
        >
          <div
            v-for="(splitSample, ss) in getSplitSamples(split)"
            :key="ss"
            :class="[
              {
                'border-bottom':
                  isInStrictSampleRange(split, splitSample.id) &&
                  genericSample.id === splitSample.id,
              },
            ]"
          >
            <span
              style="height: 20px; display: block; width: 100%"
              v-if="genericSample.id === splitSample.id"
              @mouseover="highlightSelected(splitSample)"
              @mouseleave="removeHighlights()"
            >
              <span v-if="genericSample.start_character === split.start">
                {{ findEntity(genericSample).name }}
              </span>
              <span
                v-if="
                  genericSample.end_character === split.end ||
                  genericSample.end_character === split.end - 1 ||
                  genericSample.end_character === split.end + 1
                "
                :class="[
                  'botter-trash-icon red--text pointer-cursor sample-label-icon',
                  { right: !isArabicText() },
                  { left: isArabicText() },
                ]"
                @click="openDeleteEntityDialog(splitSample)"
              />
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="isTheLastSelection(s) && showDropDown"
        class="split-dropdown z-depth-1"
      >
        <v-list subheader>
          <v-subheader>Entities</v-subheader>
          <v-list-tile>
            <v-text-field v-model="query.entity" placeholder="Search" outline />
          </v-list-tile>
          <v-list-tile
            v-for="(entity, i) in queriedEntities"
            :key="i"
            @click="selectEntity({ entity, index: s, split })"
            class="border-bottom-1px"
            style="padding-top: 6px; padding-bottom: 6px; height: auto"
          >
            <v-list-tile-content>
              <v-list-tile-title v-html="entity.name" />
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "IntentEditor",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      utterance: [],
      mouseDown: false,
      show: true,
      showDropDown: false,
      deleteEntityDialog: false,
      selectedEntity: null,
      samples: [],
      loaders: {
        delete: false,
        add: false,
      },
      query: {
        entity: "",
      },
    };
  },
  computed: {
    selectionsIndexes() {
      return this.getSelectedIndexes();
    },
    firstSelection() {
      const selections = this.getSelectedIndexes();
      if (!selections || !selections.length) return null;
      return Math.min(...this.getSelectedIndexes());
    },
    lastSelection() {
      const selections = this.getSelectedIndexes();
      if (!selections || !selections.length) return null;
      return Math.max(...this.getSelectedIndexes());
    },
    queriedEntities() {
      return this.$store.getters["nlp/entities/query"](
        this.query.entity
      ).filter((e) => e.type === "machine_learning");
    },
  },
  watch: {
    selectionsIndexes: {
      deep: true,
      handler(val) {
        this.showDropDown = true;
        let i;
        const result = [];
        for (i = this.firstSelection; i < this.lastSelection; i += 1) {
          this.utterance[i].selected = true;
        }
      },
    },
  },
  mounted() {
    this.getSamples();
    this.utterance = this.splitUtterance();
    const vm = this;
    this.$root.$on("sample-pagination", () => {
      this.show = false;
      setTimeout(() => {
        this.show = true;
        this.$forceUpdate();
      }, 500);
    });
    setTimeout(() => {
      // detect outside clicks
      window.addEventListener("click", (e) => {
        if (vm.$refs.contaier && !vm.$refs.contaier.contains(e.target)) {
          vm.showDropDown = false;
        }
      });
    }, 1000);
  },
  methods: {
    getSplitSamples(split) {
      return this.samples.filter((sample) =>
        this.isInStrictSampleRange(split, sample.id)
      );
    },
    findEntity(sample) {
      const filter = this.$store.state.nlp.entities.filter(
        (j) => j.id === sample.entity
      );
      return filter && filter.length ? filter[0] : undefined;
    },
    getSamples() {
      this.$http_nlp
        .get(
          `/apps/${this.$store.state.nlp.selectedApp.id}/intent_entity_labels`,
          { params: { "filter[intent_sample]": this.item.id } }
        )
        .then((res) => {
          this.samples = res.data.data.result;
        });
    },
    isInSampleRange(split) {
      return (
        this.samples &&
        this.samples.filter((sample) => this.inRange(split, sample)).length
      );
    },
    isInStrictSampleRange(split, sampleId) {
      return (
        this.samples &&
        this.samples.filter(
          (sample) => this.inRange(split, sample) && sample.id === sampleId
        ).length
      );
    },
    inRange(split, sample) {
      return (
        split.start >= sample.start_character &&
        (split.end <= sample.end_character ||
          split.end === sample.end_character + 1 ||
          split.end === sample.end_character - 1)
      );
    },
    getSelectionFirstCharacter() {
      let i;
      let sum = 0;
      for (i = 0; i < this.firstSelection; i += 1) {
        if (i > 0) sum += 1;
        sum += this.utterance[i].text.length;
      }
      return this.firstSelection > 0 ? sum + 1 : sum;
    },
    getSelectionLastCharacter() {
      // let i
      // let sum = this.getSelectionFirstCharacter()
      // for (i = this.firstSelection; i <= this.lastSelection; i += 1) {
      //   if (i > this.firstSelection) sum += 1
      //   sum += this.utterance[i].text.length
      // }
      // return i === (this.utterance.length - 1) ? sum : sum - 1
      return this.utterance[this.lastSelection].end;
    },
    isTheFirstSelection(index) {
      return this.firstSelection === index;
    },
    isTheLastSelection(index) {
      return this.lastSelection === index;
    },
    getSelectedIndexes() {
      return this.utterance
        .map((t, i) => ({ ...t, index: i }))
        .filter((u) => u.selected)
        .map((j) => j.index);
    },
    splitUtterance(utterance = this.utterance) {
      let i;
      const splitted = this.item.utterance.split(" ");
      const result = [];
      let temp;
      let end;
      let sum = 0;
      for (i = 0; i < splitted.length; i += 1) {
        end =
          sum +
          (i !== splitted.length - 1
            ? splitted[i].length
            : splitted[i].length - 1);
        temp = {
          text: splitted[i].toLowerCase(),
          selected: false,
          hover: false,
          highlight: false,
          start: sum,
          end,
        };
        sum += splitted[i].length;
        if (i !== splitted.length - 1) {
          sum += 1;
        }
        result.push(temp);
      }
      return result;
    },
    handleClick({ index }) {
      this.utterance[index].selected = !this.utterance[index].selected;
    },
    handleMouseOver({ index, status }) {
      if (status && this.mouseDown) {
        this.utterance[index].selected = !this.utterance[index].selected;
        this.utterance[index].hover = false;
      } else if (!status && this.mouseDown) {
        this.utterance[index].hover = false;
      } else if (status && !this.mouseDown) {
        this.utterance[index].hover = true;
      } else if (!status && !this.mouseDown) {
        this.utterance[index].hover = false;
      }
    },
    selectEntity({ index, entity, split }) {
      const payload = { entity: entity.id, intent_sample: this.item.id };
      payload.start_character = this.getSelectionFirstCharacter();
      payload.end_character = this.getSelectionLastCharacter();
      this.$store
        .dispatch("nlp/intents/samples/entities/add", payload)
        .then(() => {
          this.getSamples();
        })
        .catch(() => {
          this.$store.dispatch("showErrorMessage", true);
        });
    },
    highlightSelected(sample) {
      this.utterance = this.utterance.map((utterance) => ({
        ...utterance,
        highlight: this.isInStrictSampleRange(utterance, sample.id),
      }));
    },
    removeHighlights() {
      this.utterance = this.utterance.map((utterance) => ({
        ...utterance,
        highlight: false,
      }));
    },
    isArabicText() {
      const chars = [
        "ا",
        "ب",
        "پ",
        "ت",
        "ث",
        "ج",
        "چ",
        "ح",
        "خ",
        "د",
        "ذ",
        "ر",
        "ز",
        "ژ",
        "ص",
        "ض",
        "ط",
        "ظ",
        "ع",
        "غ",
        "ف",
        "ق",
        "ک",
        "گ",
        "ل",
        "م",
        "ن",
        "و",
        "ه",
        "ی",
      ];
      const charsCount = this.item.utterance
        .split("")
        .filter((c) => chars.includes(c)).length;
      return charsCount > this.item.utterance.length / 5;
    },
    openDeleteEntityDialog(entity) {
      this.selectedEntity = entity;
      this.deleteEntityDialog = true;
    },
    deleteEntity() {
      this.$store.dispatch("setLoader", true);
      this.$store
        .dispatch("nlp/intents/samples/entities/delete", this.selectedEntity.id)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            text: `Deleted Successfully`,
            color: "success",
          });
          this.selected = [];
          this.$store.dispatch("setLoader", false);
          this.getSamples();
          this.deleteEntityDialog = false;
        })
        .catch((err) => {
          // console.log(err)
          this.$store.dispatch("setErrorMessage", true);
          this.$store.dispatch("setLoader", false);
        });
    },
  },
};
</script>

<style lang="css" scoped>
.intent-editor-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding-top: 4px;
}
.rtl {
  direction: rtl;
}
.selected-split {
  background-color: #1de9b6;
}
.highlighted-split {
  background-color: #ffc107;
}
.split-hovered {
  background-color: #b2ebf2;
}
.first-selection {
  border-left: 2px solid #00695c !important;
  padding-left: 4px;
}
.last-selection {
  border-right: 2px solid #00695c !important;
  padding-right: 4px;
}
.first-selection-ar {
  border-right: 2px solid #00695c !important;
  padding-right: 4px;
}
.last-selection-ar {
  border-left: 2px solid #00695c !important;
  padding-left: 4px;
}
.split-item {
  display: inline-block;
  min-height: 25px;
}
.split-top-level {
  height: 10px;
}
.split-second-level {
  min-height: 10px;
  margin-bottom: 4px;
}
.text-split {
  display: flex;
  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 1px;
  padding-right: 1px;
  transition: background-color linear 0.2s;
  height: auto;
  width: 100%;
}
.split-dropdown {
  position: absolute;
  border: 1px solid #f1f1f1;
  width: 300px;
  border-radius: 8px;
  margin-left: 15px;
  z-index: 2;
}
.sample-label {
  border-radius: 4px;
  padding: 3px 4px;
  margin-top: 2px;
  margin-bottom: 2px;
}
.sample-label:hover {
  background-color: #b0bec5;
}
.sample-label-icon {
  margin-top: 2px;
  margin-left: 2px;
  margin-right: 2px;
}
.border-top {
  border-top: 2px solid #90a4ae;
}
.border-bottom {
  border-bottom: 2px solid #90a4ae;
}
.border-bottom-1px {
  border-bottom: 1px solid #f1f1f1;
}
.z-depth-1,
nav,
.card-panel,
.card,
.toast,
.btn,
.btn-large,
.btn-small,
.btn-floating,
.dropdown-content,
.collapsible,
.sidenav {
  -webkit-box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2);
}
.z-depth-2 {
  -webkit-box-shadow: 0 4px 5px 0 rgba(0, 0, 0, 0.14),
    0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 2px 4px -1px rgba(0, 0, 0, 0.3);
  box-shadow: 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12),
    0 2px 4px -1px rgba(0, 0, 0, 0.3);
}
</style>
